import React, { useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Checkbox,
  message,
} from "antd";
import { ref, set, get } from "firebase/database";
import { rtdb } from "../../../../firebase.config";

const LeaseForm = ({ visible, onClose, application }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!application) return;

    const leaseRef = ref(rtdb, `leases/${application.applicationId}`);

    get(leaseRef)
      .then((snap) => {
        if (snap.exists()) {
          const lease = snap.val();
          form.setFieldsValue({
            tenantName: application.applicantName || "",
            address: application.propertyTitle || "",
            rent: lease.rent || 0,
            deposit: lease.securityDeposit || 0,
            startDate: lease.startDate ? dayjs(lease.startDate) : null,
            petsAllowed: lease.petsAllowed || false,
          });
        } else {
          form.setFieldsValue({
            tenantName: application.applicantName || "",
            address: application.propertyTitle || "",
            rent: application.rent || 0,
            deposit: 0,
            startDate: null,
            petsAllowed: false,
          });
        }
      })
      .catch((err) => {
        console.error("Error loading lease:", err);
        message.error("Could not load lease data.");
      });
  }, [application, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const leaseRef = ref(rtdb, `leases/${application.applicationId}`);
      await set(leaseRef, {
        applicationId: application.applicationId,
        landlordId: application.landlordId,
        tenantId: application.tenantId,
        rent: values.rent || 0,
        securityDeposit: values.deposit || 0,
        startDate: values.startDate ? values.startDate.valueOf() : null,
        petsAllowed: !!values.petsAllowed,
        status: "draft",
        updatedAt: Date.now(),
      });

      message.success("Lease saved successfully");
      onClose();
    } catch (err) {
      console.error("Lease save failed:", err);
      message.error("Could not save lease. Please try again.");
    }
  };

  return (
    <Modal
      title="Add / Edit Lease"
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save Lease"
      destroyOnClose
    >
      <Form layout="vertical" form={form}>
        <Form.Item label="Tenant" name="tenantName">
          <Input disabled />
        </Form.Item>
        <Form.Item label="Property Address" name="address">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="Rent Amount"
          name="rent"
          rules={[{ required: true, message: "Rent is required" }]}
        >
          <InputNumber prefix="$" style={{ width: "100%" }} min={0} />
        </Form.Item>
        <Form.Item
          label="Security Deposit"
          name="deposit"
          rules={[{ required: true, message: "Deposit is required" }]}
        >
          <InputNumber prefix="$" style={{ width: "100%" }} min={0} />
        </Form.Item>
        <Form.Item
          label="Lease Start Date"
          name="startDate"
          rules={[{ required: true, message: "Start date is required" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="petsAllowed" valuePropName="checked">
          <Checkbox>Pets Allowed</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

LeaseForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  application: PropTypes.shape({
    applicationId: PropTypes.string.isRequired,
    landlordId: PropTypes.string.isRequired,
    tenantId: PropTypes.string.isRequired,
    applicantName: PropTypes.string,
    propertyTitle: PropTypes.string,
    rent: PropTypes.number,
    securityDeposit: PropTypes.number,
    moveInDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    petsAllowed: PropTypes.bool,
  }),
};

LeaseForm.defaultProps = {
  application: null,
};

export default LeaseForm;
